import React, { useState, useEffect, useRef } from 'react';
import { collection, addDoc, getDocs, doc, deleteDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../services/firebase';
import { Navigate } from 'react-router-dom';
import Footer from '../components/Footer';

// Dedicated Read-Only Marketing & Promotions Portal for Students and Employers
const StudentEmployerMarketingPortal = ({ banners, loading }) => {
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Helper function to calculate age info (New vs Old / Ongoing)
  const getBannerAgeInfo = (createdAt) => {
    if (!createdAt) {
      return { isNew: false, label: '🌟 Ongoing Offer', timeAgo: 'Active Campaign', dateString: 'Recent' };
    }
    let dateObj;
    if (createdAt.toDate) {
      dateObj = createdAt.toDate();
    } else if (createdAt.seconds) {
      dateObj = new Date(createdAt.seconds * 1000);
    } else if (typeof createdAt === 'string' || typeof createdAt === 'number') {
      dateObj = new Date(createdAt);
    } else {
      dateObj = new Date();
    }

    const now = new Date();
    const diffTime = Math.abs(now - dateObj);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const isNew = diffDays <= 7;

    let timeAgo = '';
    if (diffDays === 0) {
      timeAgo = 'Added Today';
    } else if (diffDays === 1) {
      timeAgo = 'Added Yesterday';
    } else if (diffDays < 30) {
      timeAgo = `Added ${diffDays} days ago`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      timeAgo = `Added ${months} ${months === 1 ? 'month' : 'months'} ago`;
    } else {
      const years = Math.floor(diffDays / 365);
      timeAgo = `Added ${years} ${years === 1 ? 'year' : 'years'} ago`;
    }

    return {
      isNew,
      label: isNew ? '🔥 New Arrival' : '🌟 Ongoing Offer',
      timeAgo,
      dateString: dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    };
  };

  const activeBanners = banners.filter(b => b.status === 'Active').sort((a, b) => {
    if (a.order !== undefined && b.order !== undefined) {
      if (a.order !== b.order) return a.order - b.order;
    }
    if (a.order !== undefined && b.order === undefined) return -1;
    if (a.order === undefined && b.order !== undefined) return 1;

    if (a.isPriority && !b.isPriority) return -1;
    if (!a.isPriority && b.isPriority) return 1;
    const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
    const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
    return timeB - timeA;
  });

  const filteredBanners = activeBanners.filter(b => {
    return !search || (b.title && b.title.toLowerCase().includes(search.toLowerCase()));
  });

  const totalPages = Math.ceil(filteredBanners.length / itemsPerPage);
  const currentBanners = filteredBanners.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm font-semibold text-gray-600">Loading Promotions & Campaigns...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-between">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12 w-full flex-1">
        {/* Executive Page Header */}
        <div className="bg-gradient-to-r from-slate-900 via-blue-950 to-indigo-950 rounded-3xl p-6 sm:p-10 text-white shadow-xl mb-8 flex flex-col md:flex-row md:items-center justify-between gap-6 border border-gray-800">
          <div>
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-blue-600/90 backdrop-blur-md text-white text-xs font-black uppercase tracking-wider shadow-sm mb-3">
              <span className="w-2 h-2 rounded-full bg-yellow-300 animate-pulse"></span>
              Official Promotions Center
            </div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Marketing & Advertisements</h1>
            <p className="text-blue-200 text-sm sm:text-base mt-2 max-w-2xl">
              Explore exclusive UdaanJobs career fairs, employer announcements, subscription package offers, and promotional campaigns.
            </p>
          </div>
          <div className="flex items-center gap-3 self-start md:self-center bg-white/10 backdrop-blur-md px-5 py-3 rounded-2xl border border-white/15 shadow-lg">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-yellow-400 to-orange-500 flex items-center justify-center text-white font-black text-lg shadow-md">
              ⚡
            </div>
            <div>
              <div className="text-xl font-extrabold text-white">{activeBanners.length}</div>
              <div className="text-xs text-blue-200 uppercase font-semibold">Active Offers</div>
            </div>
          </div>
        </div>

        {/* Clean Search Toolbar */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-xs border border-gray-100 mb-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-3 h-3 rounded-full bg-blue-600 animate-pulse"></div>
            <h2 className="text-base sm:text-lg font-extrabold text-gray-900 tracking-tight">Available Promotional Campaigns</h2>
          </div>

          <div className="relative w-full sm:w-80">
            <input
              type="text"
              placeholder="Search campaigns by title..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 shadow-2xs font-medium"
            />
            <svg className="w-4 h-4 text-gray-400 absolute left-3.5 top-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 text-xs font-bold"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Campaigns Grid Display */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {currentBanners.map((banner, idx) => {
            const ageInfo = getBannerAgeInfo(banner.createdAt);
            return (
              <div
                key={banner.id || idx}
                onClick={() => setSelectedBanner(banner)}
                className="group bg-white rounded-3xl overflow-hidden border border-gray-200/80 shadow-sm hover:shadow-2xl hover:border-blue-400 transition-all duration-300 flex flex-col justify-between cursor-pointer transform hover:-translate-y-1.5"
              >
                <div className="relative w-full overflow-hidden bg-white">
                  {banner.isPriority && (
                    <div className="absolute top-3 left-3 z-20 bg-gradient-to-r from-amber-500 to-yellow-500 text-white font-black text-[10px] uppercase tracking-wider px-3 py-1 rounded-full shadow-lg flex items-center gap-1 border border-yellow-300">
                      <span>👑 First Priority (VIP)</span>
                    </div>
                  )}
                  <img
                    src={banner.imageUrl}
                    alt={banner.title || 'Promotional Banner'}
                    className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.02]"
                  />
                </div>
                <div className="p-5 bg-white border-t border-gray-100 flex items-center justify-between">
                  <div className="truncate pr-3">
                    <span className="font-extrabold text-gray-900 text-base block truncate group-hover:text-blue-600 transition-colors">
                      {banner.title && banner.title.trim() !== 'Untitled Banner' ? banner.title : `Promotional Offer #${idx + 1}`}
                    </span>
                    <span className="text-xs text-gray-400 font-medium mt-0.5 block">Added on {ageInfo.dateString}</span>
                  </div>
                  <span className="text-xs text-blue-600 font-extrabold bg-blue-50 hover:bg-blue-100 px-3.5 py-2 rounded-xl transition-colors whitespace-nowrap shadow-2xs">
                    Zoom 🔍
                  </span>
                </div>
              </div>
            );
          })}
          {filteredBanners.length === 0 && (
            <div className="col-span-full text-center py-20 bg-white rounded-3xl border border-dashed border-gray-200">
              <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
                📢
              </div>
              <h3 className="text-lg font-bold text-gray-800">No Advertisements Found</h3>
              <p className="text-sm text-gray-500 mt-1">There are no promotional campaigns matching your current search or filter criteria.</p>
            </div>
          )}
        </div>

        {/* Professional Pagination Controls for 50+ Banners */}
        {totalPages > 1 && (
          <div className="mt-10 bg-white rounded-2xl p-4 sm:p-5 border border-gray-200/80 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="text-xs font-bold text-gray-500">
              Showing <span className="text-gray-900 font-extrabold">{((currentPage - 1) * itemsPerPage) + 1}</span> to <span className="text-gray-900 font-extrabold">{Math.min(currentPage * itemsPerPage, filteredBanners.length)}</span> of <span className="text-blue-600 font-extrabold">{filteredBanners.length}</span> active campaigns
            </span>

            <div className="flex items-center justify-between sm:justify-center gap-2 w-full sm:w-auto">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 rounded-xl border border-gray-200 text-xs font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer shadow-2xs"
              >
                ← Previous
              </button>

              {/* Page Numbers - visible on desktop/tablet, hidden on mobile */}
              <div className="hidden sm:flex items-center gap-1 px-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 rounded-lg text-xs font-extrabold transition-all cursor-pointer ${
                      currentPage === page
                        ? 'bg-blue-600 text-white shadow-xs'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              {/* Mobile page indicator */}
              <span className="sm:hidden text-xs font-bold text-gray-700 px-2">
                Page {currentPage} of {totalPages}
              </span>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 rounded-xl border border-gray-200 text-xs font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer shadow-2xs"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {selectedBanner && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-8 animate-in fade-in duration-200"
          onClick={() => setSelectedBanner(null)}
        >
          <div 
            className="relative max-w-5xl w-full bg-slate-900 rounded-3xl overflow-hidden shadow-2xl border border-white/10 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 bg-black/60 backdrop-blur-md flex items-center justify-between border-b border-white/10">
              <div className="flex items-center gap-2.5">
                <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-blue-600 text-white">
                  Promotional Offer
                </span>
                <span className="text-white font-bold text-sm sm:text-base pr-4 truncate">
                  {selectedBanner.title && selectedBanner.title.trim() !== 'Untitled Banner' ? selectedBanner.title : 'Promotional Flyer Preview'}
                </span>
              </div>
              <button
                onClick={() => setSelectedBanner(null)}
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors cursor-pointer text-sm font-bold border border-white/10"
              >
                ✕
              </button>
            </div>
            <div className="relative w-full max-h-[75vh] flex items-center justify-center bg-black overflow-hidden p-2 sm:p-4">
              <img
                src={selectedBanner.imageUrl}
                alt={selectedBanner.title || 'Advertisement Promotion'}
                className="max-w-full max-h-[70vh] object-contain rounded-xl shadow-2xl"
              />
            </div>
            <div className="p-4 bg-black/60 backdrop-blur-md flex items-center justify-between border-t border-white/10 text-xs text-gray-400">
              <span>Added on {getBannerAgeInfo(selectedBanner.createdAt).dateString} • Click anywhere outside to close</span>
              <button
                onClick={() => setSelectedBanner(null)}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl transition-colors cursor-pointer shadow-md"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

const MarketingPage = ({ user, userData }) => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  
  // Upload Form State
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState('Active'); // Active / Inactive
  const [isPriority, setIsPriority] = useState(false); // VIP First Priority
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [formError, setFormError] = useState('');

  // Filter & Search State
  const [filterStatus, setFilterStatus] = useState('All'); // All, Active, Inactive
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [adminPage, setAdminPage] = useState(1);
  const adminItemsPerPage = 12;
  const [deletingId, setDeletingId] = useState(null);
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const fileInputRef = useRef(null);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 4000);
  };

  // Fetch Banners from Firestore
  const fetchBanners = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'advertisement_banners'));
      const fetchedBanners = [];
      querySnapshot.forEach((docSnap) => {
        fetchedBanners.push({
          id: docSnap.id,
          ...docSnap.data()
        });
      });
      // Sort in memory: By order ascending (if available), then Priority banners first, then by createdAt descending
      fetchedBanners.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          if (a.order !== b.order) return a.order - b.order;
        }
        if (a.order !== undefined && b.order === undefined) return -1;
        if (a.order === undefined && b.order !== undefined) return 1;

        if (a.isPriority && !b.isPriority) return -1;
        if (!a.isPriority && b.isPriority) return 1;
        const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return timeB - timeA;
      });
      setBanners(fetchedBanners);
    } catch (error) {
      console.error('Error fetching banners:', error);
      showToast('Failed to load banners. Please check your connection.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  // Handle Image Selection & Preview
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setFormError('Please select a valid image file (PNG, JPG, WEBP).');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setFormError('Image file size must be less than 5MB.');
        return;
      }
      setFormError('');
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const resetForm = () => {
    setTitle('');
    setStatus('Active');
    setIsPriority(false);
    setImageFile(null);
    setImagePreview('');
    setFormError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Upload Banner to Firebase Storage & Firestore
  const handleUploadBanner = async (e) => {
    e.preventDefault();
    if (!imageFile) {
      setFormError('Banner image is required. Please select an image.');
      return;
    }

    setUploading(true);
    setFormError('');

    try {
      // 1. Upload to Firebase Storage
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `banners/${Date.now()}_${Math.random().toString(36).substring(2, 8)}.${fileExt}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, imageFile);
      const downloadUrl = await getDownloadURL(storageRef);

      // 2. Save metadata to Firestore
      const newBannerData = {
        title: title.trim() || 'Untitled Banner',
        imageUrl: downloadUrl,
        storagePath: fileName,
        status: status,
        isPriority: isPriority,
        createdAt: serverTimestamp(),
        createdBy: user?.email || 'superadmin'
      };

      const docRef = await addDoc(collection(db, 'advertisement_banners'), newBannerData);

      // Add to local state instantly
      const addedBanner = {
        id: docRef.id,
        ...newBannerData,
        createdAt: { toMillis: () => Date.now() }
      };

      setBanners([addedBanner, ...banners]);
      showToast('Advertisement Banner uploaded and published successfully!');
      resetForm();
      setShowUploadModal(false);
    } catch (error) {
      console.error('Error uploading banner:', error);
      setFormError('Failed to upload banner: ' + (error.message || 'Unknown error'));
    } finally {
      setUploading(false);
    }
  };

  // Delete Banner
  const handleDeleteBanner = async (banner) => {
    if (!window.confirm(`Are you sure you want to permanently delete banner "${banner.title}"?`)) {
      return;
    }

    setDeletingId(banner.id);
    try {
      // 1. Delete file from Storage if storagePath exists
      if (banner.storagePath) {
        try {
          const fileRef = ref(storage, banner.storagePath);
          await deleteObject(fileRef);
        } catch (storageErr) {
          console.warn('Storage file deletion note:', storageErr);
        }
      }

      // 2. Delete doc from Firestore
      await deleteDoc(doc(db, 'advertisement_banners', banner.id));

      setBanners(banners.filter((b) => b.id !== banner.id));
      showToast('Banner deleted successfully.');
    } catch (error) {
      console.error('Error deleting banner:', error);
      showToast('Failed to delete banner: ' + error.message, 'error');
    } finally {
      setDeletingId(null);
    }
  };

  // Toggle Status (Active/Inactive)
  const handleToggleStatus = async (banner) => {
    const newStatus = banner.status === 'Active' ? 'Inactive' : 'Active';
    try {
      await updateDoc(doc(db, 'advertisement_banners', banner.id), {
        status: newStatus
      });

      setBanners(banners.map((b) => (b.id === banner.id ? { ...b, status: newStatus } : b)));
      showToast(`Status changed to ${newStatus} for "${banner.title}".`);
    } catch (error) {
      console.error('Error updating banner status:', error);
      showToast('Failed to update status: ' + error.message, 'error');
    }
  };

  // Toggle Priority (Top Placement for Extra Money / VIP Advertisers)
  const handleTogglePriority = async (banner) => {
    const newPriority = !banner.isPriority;
    try {
      await updateDoc(doc(db, 'advertisement_banners', banner.id), {
        isPriority: newPriority
      });

      setBanners(banners.map((b) => (b.id === banner.id ? { ...b, isPriority: newPriority } : b)));
      showToast(`Priority ${newPriority ? 'set to TOP PRIORITY ⭐' : 'removed'} for "${banner.title}".`);
    } catch (error) {
      console.error('Error updating priority:', error);
      showToast('Failed to update priority: ' + error.message, 'error');
    }
  };

  // Handle Drag & Drop Reordering
  const handleDropBanner = async (targetBanner) => {
    if (!draggedId || draggedId === targetBanner.id) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }

    const fromIndex = banners.findIndex(b => b.id === draggedId);
    const toIndex = banners.findIndex(b => b.id === targetBanner.id);
    
    if (fromIndex === -1 || toIndex === -1) return;

    const updatedBanners = [...banners];
    const [movedItem] = updatedBanners.splice(fromIndex, 1);
    updatedBanners.splice(toIndex, 0, movedItem);

    // Reassign sequential order numbers
    const reordered = updatedBanners.map((item, idx) => ({
      ...item,
      order: idx + 1
    }));

    setBanners(reordered);
    setDraggedId(null);
    setDragOverId(null);

    // Save order to Firestore
    try {
      showToast('Saving new campaign placement order... ⏳');
      const updatePromises = reordered.map((item, idx) => 
        updateDoc(doc(db, 'advertisement_banners', item.id), { order: idx + 1 })
      );
      await Promise.all(updatePromises);
      showToast('Campaign placement order updated successfully! ⭐');
    } catch (error) {
      console.error('Error reordering banners:', error);
      showToast('Failed to save reorder: ' + error.message, 'error');
    }
  };

  // Filtered banners
  const filteredBanners = banners.filter((b) => {
    const matchesStatus = filterStatus === 'All' || 
      (filterStatus === 'Priority VIP' ? Boolean(b.isPriority) : b.status === filterStatus);
    const matchesSearch = !searchQuery.trim() || (b.title && b.title.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesStatus && matchesSearch;
  });

  const totalAdminPages = Math.ceil(filteredBanners.length / adminItemsPerPage);
  const currentAdminBanners = filteredBanners.slice((adminPage - 1) * adminItemsPerPage, adminPage * adminItemsPerPage);

  const activeCount = banners.filter(b => b.status === 'Active').length;
  const inactiveCount = banners.filter(b => b.status === 'Inactive').length;
  const priorityCount = banners.filter(b => Boolean(b.isPriority)).length;

  if (userData && userData.userType !== 'superadmin') {
    return <StudentEmployerMarketingPortal banners={banners} loading={loading} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-between">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed bottom-6 right-6 z-50 px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 border text-white animate-bounce ${
          toast.type === 'error' ? 'bg-red-600 border-red-500' : 'bg-gray-900 border-gray-800'
        }`}>
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${toast.type === 'error' ? 'bg-white/20 text-white' : 'bg-green-500 text-white'}`}>
            {toast.type === 'error' ? '✕' : '✓'}
          </div>
          <span className="text-sm font-semibold">{toast.message}</span>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12 w-full flex-1">
        
        {/* Executive Header Card */}
        <div className="bg-slate-900 rounded-3xl p-6 sm:p-8 text-white shadow-xl mb-8 flex flex-col md:flex-row md:items-center justify-between gap-6 border border-slate-800 relative overflow-hidden">
          <div className="absolute right-0 top-0 bottom-0 w-96 bg-gradient-to-l from-blue-600/10 via-indigo-600/5 to-transparent pointer-events-none"></div>
          
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 text-blue-400 text-xs font-bold uppercase tracking-wider mb-3 border border-slate-700/80">
              <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
              Super Admin Control Center
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">Advertisement & Marketing Banners</h1>
            <p className="text-slate-400 text-sm mt-1.5 max-w-2xl">
              Create, publish, and manage promotional campaigns displayed across the student and employer portals.
            </p>
          </div>

          <button
            onClick={() => {
              resetForm();
              setShowUploadModal(true);
            }}
            className="relative z-10 bg-blue-600 hover:bg-blue-500 text-white font-bold px-6 py-3 rounded-xl shadow-lg hover:shadow-blue-500/25 transition-all duration-200 flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm self-start md:self-center border border-blue-400/30"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
            <span>Add Banner</span>
          </button>
        </div>

        {/* Professional Executive Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Card 1: Total Banners */}
          <div 
            onClick={() => setFilterStatus('All')}
            className="group bg-gradient-to-br from-blue-50/90 via-sky-50/50 to-white rounded-3xl p-6 border border-blue-200/80 shadow-sm hover:shadow-2xl hover:shadow-blue-500/10 hover:-translate-y-2 hover:border-blue-300 transition-all duration-300 ease-out flex items-center justify-between relative overflow-hidden cursor-pointer"
          >
            <div className="absolute -right-6 -bottom-6 w-24 h-24 bg-gradient-to-br from-blue-400/20 to-sky-500/20 rounded-full blur-xl group-hover:scale-150 transition-transform duration-500 pointer-events-none"></div>
            <div className="relative z-10">
              <p className="text-blue-700 text-xs font-extrabold uppercase tracking-wider flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                Total Banners
              </p>
              <h3 className="text-3xl sm:text-4xl font-black text-slate-900 mt-1.5 group-hover:text-blue-600 transition-colors duration-300">{banners.length}</h3>
              <p className="text-slate-600 text-xs mt-1 font-semibold">All uploaded promotional campaigns</p>
            </div>
            <div className="relative z-10 w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-2xl font-bold shadow-md shadow-blue-500/25 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shrink-0">
              🖼️
            </div>
          </div>

          {/* Card 2: Active Campaigns */}
          <div 
            onClick={() => setFilterStatus('Active')}
            className="group bg-gradient-to-br from-emerald-50/90 via-teal-50/50 to-white rounded-3xl p-6 border border-emerald-200/80 shadow-sm hover:shadow-2xl hover:shadow-emerald-500/10 hover:-translate-y-2 hover:border-emerald-300 transition-all duration-300 ease-out flex items-center justify-between relative overflow-hidden cursor-pointer"
          >
            <div className="absolute -right-6 -bottom-6 w-24 h-24 bg-gradient-to-br from-emerald-400/20 to-teal-500/20 rounded-full blur-xl group-hover:scale-150 transition-transform duration-500 pointer-events-none"></div>
            <div className="relative z-10">
              <p className="text-emerald-700 text-xs font-extrabold uppercase tracking-wider flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                Active Campaigns
              </p>
              <h3 className="text-3xl sm:text-4xl font-black text-slate-900 mt-1.5 group-hover:text-emerald-600 transition-colors duration-300">{activeCount}</h3>
              <p className="text-emerald-700 text-xs mt-1 font-bold flex items-center gap-1">● Currently visible in portal</p>
            </div>
            <div className="relative z-10 w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center text-2xl font-bold shadow-md shadow-emerald-500/25 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 shrink-0">
              ✓
            </div>
          </div>

          {/* Card 3: Inactive Banners */}
          <div 
            onClick={() => setFilterStatus('Inactive')}
            className="group bg-gradient-to-br from-amber-50/90 via-orange-50/50 to-white rounded-3xl p-6 border border-amber-200/80 shadow-sm hover:shadow-2xl hover:shadow-amber-500/10 hover:-translate-y-2 hover:border-amber-300 transition-all duration-300 ease-out flex items-center justify-between relative overflow-hidden cursor-pointer"
          >
            <div className="absolute -right-6 -bottom-6 w-24 h-24 bg-gradient-to-br from-amber-400/20 to-orange-500/20 rounded-full blur-xl group-hover:scale-150 transition-transform duration-500 pointer-events-none"></div>
            <div className="relative z-10">
              <p className="text-amber-700 text-xs font-extrabold uppercase tracking-wider flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                Inactive Banners
              </p>
              <h3 className="text-3xl sm:text-4xl font-black text-slate-900 mt-1.5 group-hover:text-amber-600 transition-colors duration-300">{inactiveCount}</h3>
              <p className="text-amber-700 text-xs mt-1 font-bold">⏸ Paused or archived</p>
            </div>
            <div className="relative z-10 w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 text-white flex items-center justify-center text-2xl font-bold shadow-md shadow-amber-500/25 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shrink-0">
              ⏸
            </div>
          </div>

          {/* Card 4: Top Priority (VIP) */}
          <div 
            onClick={() => setFilterStatus('Priority VIP')}
            className="group bg-gradient-to-br from-purple-50/90 via-fuchsia-50/50 to-white rounded-3xl p-6 border border-purple-200/80 shadow-sm hover:shadow-2xl hover:shadow-purple-500/10 hover:-translate-y-2 hover:border-purple-300 transition-all duration-300 ease-out flex items-center justify-between relative overflow-hidden cursor-pointer"
          >
            <div className="absolute -right-6 -bottom-6 w-24 h-24 bg-gradient-to-br from-purple-400/20 to-fuchsia-500/20 rounded-full blur-xl group-hover:scale-150 transition-transform duration-500 pointer-events-none"></div>
            <div className="relative z-10">
              <p className="text-purple-700 text-xs font-extrabold uppercase tracking-wider flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                Top Priority (VIP)
              </p>
              <h3 className="text-3xl sm:text-4xl font-black text-slate-900 mt-1.5 group-hover:text-purple-600 transition-colors duration-300">{priorityCount}</h3>
              <p className="text-purple-700 text-xs mt-1 font-bold">👑 Paid first priority placement</p>
            </div>
            <div className="relative z-10 w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-600 to-fuchsia-600 text-white flex items-center justify-center text-2xl font-bold shadow-md shadow-purple-500/25 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 shrink-0">
              👑
            </div>
          </div>
        </div>

        {/* Filters & Search Bar */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-xs border border-gray-100 mb-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-2 bg-gray-100 p-1 rounded-xl self-start">
              {['All', 'Active', 'Inactive', 'Priority VIP'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setFilterStatus(tab);
                    setAdminPage(1);
                  }}
                  className={`px-4 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    filterStatus === tab
                      ? 'bg-white text-blue-600 shadow-2xs font-extrabold'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab} ({tab === 'All' ? banners.length : tab === 'Active' ? activeCount : tab === 'Priority VIP' ? priorityCount : inactiveCount})
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto">
              {/* View Mode Toggle */}
              <div className="flex items-center bg-gray-100 p-1 rounded-xl shrink-0">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                    viewMode === 'grid' ? 'bg-white text-blue-600 shadow-2xs' : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Grid View"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                  <span className="hidden md:inline">Grid</span>
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-2 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                    viewMode === 'table' ? 'bg-white text-blue-600 shadow-2xs' : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Table List View"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                  <span className="hidden md:inline">Table List</span>
                </button>
              </div>

              <div className="relative flex-1 sm:w-64">
                <input
                  type="text"
                  placeholder="Search banners by title..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setAdminPage(1);
                  }}
                  className="w-full pl-9 pr-4 py-2 bg-gray-50 rounded-xl border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-blue-600 shadow-2xs font-medium"
                />
                <svg className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 text-xs font-bold"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>

        {/* Banners List / Grid Display */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-gray-100">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-500 text-xs mt-3 font-medium">Loading advertisement campaigns...</p>
          </div>
        ) : filteredBanners.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-200 p-8">
            <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
              📢
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">No Advertisement Banners Found</h3>
            <p className="text-gray-500 text-xs max-w-md mx-auto mb-6">
              {searchQuery || filterStatus !== 'All'
                ? 'No banners matched your search query or filter criteria.'
                : 'Get started by creating your first promotional advertisement banner for the portal.'}
            </p>
            {!searchQuery && filterStatus === 'All' && (
              <button
                onClick={() => {
                  resetForm();
                  setShowUploadModal(true);
                }}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-6 py-3 rounded-xl shadow-md hover:shadow transition-all cursor-pointer"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Add Banner</span>
              </button>
            )}
          </div>
        ) : viewMode === 'table' ? (
          /* Executive Table List View for 50+ Banners */
          <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-200/80 text-[11px] font-extrabold text-gray-500 uppercase tracking-wider">
                    <th className="py-3.5 px-5">Promotional Campaign</th>
                    <th className="py-3.5 px-5">Visibility Status</th>
                    <th className="py-3.5 px-5">Uploaded Date</th>
                    <th className="py-3.5 px-5 text-right">Quick Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {currentAdminBanners.map((banner) => (
                    <tr 
                      key={banner.id} 
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = 'move';
                        setDraggedId(banner.id);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        if (dragOverId !== banner.id) setDragOverId(banner.id);
                      }}
                      onDragLeave={() => {
                        if (dragOverId === banner.id) setDragOverId(null);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        handleDropBanner(banner);
                      }}
                      className={`transition-all group ${
                        dragOverId === banner.id 
                          ? 'bg-blue-100/90 border-2 border-blue-500 shadow-lg scale-[1.01]' 
                          : draggedId === banner.id 
                          ? 'opacity-30 bg-gray-200' 
                          : 'hover:bg-blue-50/40'
                      }`}
                    >
                      <td className="py-3.5 px-5">
                        <div className="flex items-center gap-3.5">
                          {/* 3-Line Professional Drag Grip */}
                          <div 
                            className="w-7 h-9 rounded-lg bg-gray-100 hover:bg-blue-100 border border-gray-200 hover:border-blue-300 text-gray-400 hover:text-blue-600 flex flex-col items-center justify-center gap-[3px] cursor-grab active:cursor-grabbing transition-all shrink-0 shadow-2xs group-hover:bg-white group-hover:border-gray-300"
                            title="Drag up or down to reorder placement"
                          >
                            <span className="w-3.5 h-[2px] bg-current rounded-full"></span>
                            <span className="w-3.5 h-[2px] bg-current rounded-full"></span>
                            <span className="w-3.5 h-[2px] bg-current rounded-full"></span>
                          </div>

                          <div className="w-14 h-16 rounded-lg bg-white overflow-hidden shrink-0 border border-gray-200 flex items-center justify-center relative shadow-xs">
                            <img src={banner.imageUrl} alt="" className="w-full h-full object-contain" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-gray-900 block group-hover:text-blue-600 transition-colors">
                                {banner.title && banner.title.trim() !== 'Untitled Banner' ? banner.title : 'Untitled Promotional Banner'}
                              </span>
                              {banner.isPriority && (
                                <span className="inline-flex items-center gap-1 bg-gradient-to-r from-amber-500 to-yellow-500 text-white text-[9px] font-black px-2 py-0.5 rounded-full shadow-xs uppercase tracking-wider">
                                  👑 VIP Priority
                                </span>
                              )}
                            </div>
                            <span className="text-[11px] text-gray-400 font-medium">ID: {banner.id?.substring(0, 8)}...</span>
                          </div>
                        </div>
                      </td>
                      <td className="py-3.5 px-5">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-extrabold tracking-wider uppercase ${
                            banner.status === 'Active'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-amber-50 text-amber-700 border border-amber-200'
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${banner.status === 'Active' ? 'bg-emerald-500' : 'bg-amber-500'}`}></span>
                          {banner.status}
                        </span>
                      </td>
                      <td className="py-3.5 px-5 text-xs font-medium text-gray-500">
                        {banner.createdAt?.toDate ? banner.createdAt.toDate().toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric'
                        }) : 'Recent'}
                      </td>
                      <td className="py-3.5 px-5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleTogglePriority(banner)}
                            className={`px-3 py-1.5 rounded-xl text-xs font-extrabold transition-all cursor-pointer flex items-center gap-1 ${
                              banner.isPriority
                                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md hover:from-purple-700 hover:to-indigo-700 ring-2 ring-purple-300'
                                : 'bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200/60'
                            }`}
                            title="Set First Priority placement for extra money paid"
                          >
                            {banner.isPriority ? '👑 Top Priority' : '⭐ Set Priority'}
                          </button>
                          <button
                            onClick={() => handleToggleStatus(banner)}
                            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                              banner.status === 'Active'
                                ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200/60'
                                : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200/60'
                            }`}
                          >
                            {banner.status === 'Active' ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            onClick={() => handleDeleteBanner(banner)}
                            disabled={deletingId === banner.id}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors border border-transparent hover:border-red-200 disabled:opacity-50 cursor-pointer"
                            title="Delete Banner"
                          >
                            {deletingId === banner.id ? (
                              <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentAdminBanners.map((banner) => (
              <div
                key={banner.id}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'move';
                  setDraggedId(banner.id);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (dragOverId !== banner.id) setDragOverId(banner.id);
                }}
                onDragLeave={() => {
                  if (dragOverId === banner.id) setDragOverId(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  handleDropBanner(banner);
                }}
                className={`bg-white rounded-2xl overflow-hidden border transition-all duration-300 flex flex-col justify-between group ${
                  dragOverId === banner.id 
                    ? 'border-2 border-blue-500 shadow-2xl scale-[1.02] ring-4 ring-blue-100' 
                    : draggedId === banner.id 
                    ? 'opacity-30 bg-gray-100 border-dashed border-gray-300' 
                    : 'border-gray-200/80 shadow-xs hover:shadow-lg'
                }`}
              >
                {/* Banner Preview Image */}
                <div className="relative w-full bg-white overflow-hidden">
                  <img
                    src={banner.imageUrl}
                    alt={banner.title || 'Promotional Banner'}
                    className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.02]"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/600x300?text=Image+Not+Found';
                    }}
                  />
                  
                  {/* VIP Priority Badge */}
                  {banner.isPriority && (
                    <div className="absolute top-3 left-3 z-20">
                      <span className="inline-flex items-center gap-1 bg-gradient-to-r from-amber-500 to-yellow-500 text-white font-black text-[10px] uppercase tracking-wider px-3 py-1 rounded-full shadow-lg border border-yellow-300">
                        <span>👑 VIP Priority</span>
                      </span>
                    </div>
                  )}

                  {/* Status Badge */}
                  <div className="absolute top-3 right-3 z-20">
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-extrabold tracking-wider uppercase shadow-md ${
                        banner.status === 'Active'
                          ? 'bg-emerald-500 text-white border border-emerald-400'
                          : 'bg-amber-500 text-white border border-amber-400'
                      }`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-white"></span>
                      {banner.status}
                    </span>
                  </div>
                </div>

                {/* Banner Details & Controls */}
                <div className="p-5 flex-1 flex flex-col justify-between">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-bold text-gray-900 text-base line-clamp-1 group-hover:text-blue-600 transition-colors">
                        {banner.title && banner.title.trim() !== 'Untitled Banner' ? banner.title : 'Untitled Promotional Banner'}
                      </h3>
                      <p className="text-gray-400 text-[11px] mt-1 font-medium">
                        Uploaded on {banner.createdAt?.toDate ? banner.createdAt.toDate().toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric'
                        }) : 'Recent'}
                      </p>
                    </div>

                    {/* 3-Line Professional Drag Grip */}
                    <div 
                      className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-blue-100 border border-gray-200 hover:border-blue-300 text-gray-400 hover:text-blue-600 flex flex-col items-center justify-center gap-[2.5px] cursor-grab active:cursor-grabbing transition-all shrink-0 shadow-2xs group-hover:bg-white"
                      title="Drag to reorder placement"
                    >
                      <span className="w-3.5 h-[2px] bg-current rounded-full"></span>
                      <span className="w-3.5 h-[2px] bg-current rounded-full"></span>
                      <span className="w-3.5 h-[2px] bg-current rounded-full"></span>
                    </div>
                  </div>

                  {/* Actions Bar */}
                  <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleTogglePriority(banner)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-extrabold transition-all cursor-pointer flex items-center gap-1 ${
                          banner.isPriority
                            ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md hover:from-purple-700 hover:to-indigo-700 ring-2 ring-purple-300'
                            : 'bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200/60'
                        }`}
                        title="Set First Priority placement for extra money paid"
                      >
                        {banner.isPriority ? '👑 Top Priority' : '⭐ Set Priority'}
                      </button>
                      <button
                        onClick={() => handleToggleStatus(banner)}
                        className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                          banner.status === 'Active'
                            ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200/60'
                            : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200/60'
                        }`}
                      >
                        {banner.status === 'Active' ? (
                          <>
                            <span>Deactivate</span>
                          </>
                        ) : (
                          <>
                            <span>Activate</span>
                          </>
                        )}
                      </button>
                    </div>

                    <button
                      onClick={() => handleDeleteBanner(banner)}
                      disabled={deletingId === banner.id}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors border border-transparent hover:border-red-200 disabled:opacity-50 cursor-pointer"
                      title="Delete Banner"
                    >
                      {deletingId === banner.id ? (
                        <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Super Admin Pagination Footer for 50+ Banners */}
        {totalAdminPages > 1 && (
          <div className="mt-8 bg-white rounded-2xl p-4 sm:p-5 border border-gray-200/80 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="text-xs font-bold text-gray-500">
              Showing <span className="text-gray-900 font-extrabold">{((adminPage - 1) * adminItemsPerPage) + 1}</span> to <span className="text-gray-900 font-extrabold">{Math.min(adminPage * adminItemsPerPage, filteredBanners.length)}</span> of <span className="text-blue-600 font-extrabold">{filteredBanners.length}</span> total banners
            </span>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setAdminPage(prev => Math.max(1, prev - 1))}
                disabled={adminPage === 1}
                className="px-4 py-2 rounded-xl border border-gray-200 text-xs font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer shadow-2xs"
              >
                ← Previous
              </button>

              <div className="flex items-center gap-1 px-2 flex-wrap justify-center">
                {Array.from({ length: totalAdminPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setAdminPage(page)}
                    className={`w-8 h-8 rounded-lg text-xs font-extrabold transition-all cursor-pointer ${
                      adminPage === page
                        ? 'bg-blue-600 text-white shadow-xs'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setAdminPage(prev => Math.min(totalAdminPages, prev + 1))}
                disabled={adminPage === totalAdminPages}
                className="px-4 py-2 rounded-xl border border-gray-200 text-xs font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer shadow-2xs"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Clean & Professional Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-6 sm:p-8 overflow-hidden relative border border-gray-100 animate-in fade-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-4 border-b border-gray-100 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center font-bold">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Upload Banner Image</h3>
                  <p className="text-xs text-gray-500">Add a new advertisement banner to the portal</p>
                </div>
              </div>
              <button
                onClick={() => {
                  resetForm();
                  setShowUploadModal(false);
                }}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Error Banner */}
            {formError && (
              <div className="mb-6 p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs font-medium flex items-center gap-2">
                <svg className="w-4 h-4 shrink-0 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>{formError}</span>
              </div>
            )}

            {/* Upload Form */}
            <form onSubmit={handleUploadBanner} className="space-y-5">
              {/* Image Upload Box */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                  Banner Image <span className="text-red-500">*</span>
                </label>
                
                {!imagePreview ? (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 hover:border-blue-500 rounded-2xl p-8 text-center bg-gray-50/50 hover:bg-blue-50/20 transition-all cursor-pointer group"
                  >
                    <div className="w-12 h-12 bg-white rounded-2xl shadow-sm border border-gray-100 flex items-center justify-center mx-auto mb-3 text-blue-500 group-hover:scale-110 transition-transform">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <p className="text-sm font-semibold text-gray-700">Click to select banner image</p>
                    <p className="text-xs text-gray-400 mt-1">Recommended size: 1200 x 400px (Max 5MB)</p>
                    <p className="text-[11px] text-gray-400">Supports PNG, JPG, WEBP</p>
                  </div>
                ) : (
                  <div className="relative rounded-2xl overflow-hidden border border-gray-200 bg-white group">
                    <img
                      src={imagePreview}
                      alt="Banner Preview"
                      className="w-full h-auto block max-h-[450px] object-contain mx-auto"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="px-3.5 py-1.5 bg-white text-gray-800 rounded-lg text-xs font-bold shadow-md hover:bg-gray-100 transition-colors cursor-pointer"
                      >
                        Change Image
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setImageFile(null);
                          setImagePreview('');
                        }}
                        className="px-3.5 py-1.5 bg-red-600 text-white rounded-lg text-xs font-bold shadow-md hover:bg-red-700 transition-colors cursor-pointer"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png, image/jpeg, image/webp"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </div>

              {/* Title Input */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                  Banner Title <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Diwali Mega Career Fair - Up to 50% Off Packages"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all shadow-2xs font-medium"
                />
              </div>

              {/* Status Selector */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                  Initial Visibility Status
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setStatus('Active')}
                    className={`p-3 rounded-xl border text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${
                      status === 'Active'
                        ? 'bg-emerald-50 border-emerald-500 text-emerald-700 shadow-2xs'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${status === 'Active' ? 'bg-emerald-500' : 'bg-gray-300'}`}></span>
                    Active (Visible)
                  </button>

                  <button
                    type="button"
                    onClick={() => setStatus('Inactive')}
                    className={`p-3 rounded-xl border text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${
                      status === 'Inactive'
                        ? 'bg-amber-50 border-amber-500 text-amber-700 shadow-2xs'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${status === 'Inactive' ? 'bg-amber-500' : 'bg-gray-300'}`}></span>
                    Inactive (Hidden)
                  </button>
                </div>
              </div>

              {/* VIP Priority Placement */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                  First Priority Placement (VIP Paid)
                </label>
                <div 
                  onClick={() => setIsPriority(!isPriority)}
                  className={`p-3.5 rounded-xl border flex items-center justify-between cursor-pointer transition-all ${
                    isPriority 
                      ? 'bg-purple-50 border-purple-500 text-purple-900 shadow-2xs' 
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <span className="text-xl">👑</span>
                    <div>
                      <p className="text-xs font-extrabold">Set as First Priority Advertisement</p>
                      <p className="text-[11px] text-gray-400 font-medium">For advertisers who paid extra money for top placement</p>
                    </div>
                  </div>
                  <div className={`w-6 h-6 rounded-lg flex items-center justify-center font-bold text-xs ${isPriority ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-400'}`}>
                    {isPriority ? '✓' : ''}
                  </div>
                </div>
              </div>

              {/* Modal Action Buttons */}
              <div className="pt-4 border-t border-gray-100 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setShowUploadModal(false);
                  }}
                  className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-xs font-bold hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading || !imageFile}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-xs font-bold shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 cursor-pointer"
                >
                  {uploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Uploading & Saving...</span>
                    </>
                  ) : (
                    <>
                      <span>Save & Publish Banner</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default MarketingPage;
